import { useMemo, useRef } from 'react';

import styled from 'styled-components';

import { type TransactionPaymentsFilterInput, useGetTransactionPaymentsQuery } from 'data/__generated__';

import { DataTable, type DataTableFiltersApi } from 'components/DataTable';

import { buildPaymentsColumns, type PaymentRow } from '../constants/paymentsColumns';
import { paymentPath } from './PaymentIdLink';

// Une table de paiements embarquée dans une fiche (utilisateur, paiement, moyen de paiement) : le scope est épinglé,
// l'utilisateur peut l'affiner sans jamais en sortir.
export const PaymentsSection = ({
    scope,
    hiddenColumnIds = [],
    highlightedPaymentId,
    emptyMessage,
    dataTest,
}: {
    scope: Partial<TransactionPaymentsFilterInput>;
    hiddenColumnIds?: string[];
    // Le paiement de la fiche courante, quand la section liste ses voisins.
    highlightedPaymentId?: string;
    emptyMessage: string;
    dataTest?: string;
}) => {
    const filtersApiRef = useRef<DataTableFiltersApi | null>(null);
    const hiddenColumnIdsKey = hiddenColumnIds.join(',');
    const columns = useMemo(
        () =>
            buildPaymentsColumns({ onSelectId: (id) => filtersApiRef.current?.setFilter('searchValue', id) }).filter(
                (column) => !column.id || !hiddenColumnIdsKey.split(',').includes(column.id),
            ),
        [hiddenColumnIdsKey],
    );

    const { loading, data, error, refetch, fetchMore } = useGetTransactionPaymentsQuery({
        variables: { filters: scope },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    // Erreur en ligne : la section ne doit pas faire tomber le reste de la fiche.
    if (error) {
        return <ErrorMessage>Une erreur est survenue lors de la récupération des paiements</ErrorMessage>;
    }

    return (
        <TableContainer data-test={dataTest}>
            <DataTable<PaymentRow, TransactionPaymentsFilterInput>
                columns={columns}
                data={data?.transactionPayments.transactionPayments ?? []}
                filterMode="server"
                pinnedFilters={scope}
                getRowId={(row) => row._id}
                getRowLink={(row) => paymentPath(row._id)}
                highlightedKeys={highlightedPaymentId ? [highlightedPaymentId] : undefined}
                hasMore={data?.transactionPayments.hasMore ?? false}
                isLoading={loading}
                refetch={refetch}
                fetchMore={fetchMore}
                searchPlaceholder="ID du moyen de paiement, du paiement ou de la transaction"
                searchFilterKey="searchValue"
                disableUrlSync
                autoFocusSearch={false}
                emptyMessage={emptyMessage}
                filtersApiRef={filtersApiRef}
            />
        </TableContainer>
    );
};

// Hauteur bornée : la table défile en interne, ce qui déclenche son scroll infini.
const TableContainer = styled.div`
    display: flex;
    height: 60vh;
`;

const ErrorMessage = styled.div`
    color: ${({ theme }) => theme.errorColor};
`;
